import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import { useInitializeApp } from '@/hooks/useInitializeApp'
import { useAuthStore } from '@/store/auth.store'

interface ProtectedRouteProps {
  children: ReactNode
}

/**
 * Guard de autenticação — usado para envolver todas as rotas que exigem
 * sessão ativa (JWT ou HTTP Basic).
 *
 * Conforme spec §6 ("Rota protegida") e §7 ("Roteamento"), o `ProtectedRoute`
 * wrapa o `AppShell` no roteamento e redireciona para `/login` quando não há
 * sessão ativa.
 *
 * No refresh da página (F5), o store Zustand re-inicializa com `mode: null`
 * mas o `refreshToken` pode estar persistido em localStorage. O hook
 * `useInitializeApp` realiza o bootstrap assíncrono (refresh → /me → tenant)
 * e restaura `mode: 'jwt'`. Enquanto esse bootstrap não terminar (`isReady`
 * ainda é `false`), exibimos um indicador de carregamento ao invés de
 * redirecionar prematuramente para `/login`.
 *
 * Critério de "autenticado": `auth.mode !== null`. Usamos `mode` (e não
 * apenas `accessToken`) porque a aplicação suporta dois modos de autenticação
 * mutuamente exclusivos:
 *   - `jwt`   → usuários normais, possui `accessToken`/`refreshToken`
 *   - `basic` → admin global, possui apenas `basicCredentials` (sem token JWT)
 *
 * Em ambos os casos o usuário está autenticado e deve poder acessar as rotas
 * protegidas. Verificar `accessToken` isoladamente bloquearia indevidamente o
 * admin em modo Basic.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isReady, isLoading } = useInitializeApp()
  const mode = useAuthStore((state) => state.mode)

  // Bootstrap em andamento — exibe loading ao invés de redirecionar.
  if (!isReady || isLoading) {
    return (
      <div
        data-testid="auth-loading"
        aria-busy="true"
        aria-live="polite"
        className="flex h-screen w-screen items-center justify-center text-sm text-muted-foreground"
      >
        Carregando…
      </div>
    )
  }

  // Bootstrap concluído e não há sessão ativa → redireciona para login.
  if (mode === null) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}
